import { Card, Col, Row, Tabs, Tooltip, Typography } from "antd";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import { useRolesContext } from "../../../context/RolesContext";
import useHttp from "../../../hooks/useHttp";
import useNumbers from "../../../hooks/useNumbers";
import { successNotification } from "../../../pages/utils/CommonNotifications";
import { getContactAvatar, getDisplayNameOfCustomer } from "../../../pages/utils/CommonVessels";
import { checkUserHasModuleAccess } from "../../../pages/utils/commonUtils";
import Link from "../../custom/info/Link";
import ContactDetails from "./ContactDetails";
import Notes from "./Notes";
import HNotification from "../../custom/notification/HNotification";
import { ConvContext } from "../../../pages/ConversationContext";

const { Text } = Typography;

const ContactInfoPanel = ({
  contactId,
  leftServiceId,
  onClose,
  showContactInfo,
  handleBlockNumberSuccess = () => {},
  showBlockNumberBtn = true,
  sourceModule,
  sourceId,
  toNumber,
  showSwitch,
  showChatView,
  setShowChatView,
  fromNumber,
  onNoteAdded,
  onNoteUpdated,
  onNoteDeleted,
  noteIdToBeFocused,
  setNoteIdToBeFocused,
}) => {
  const [appProperties] = useContext(AppContext);
  const { fetchData } = useHttp();
  const [contactData, setContactData] = useState(null);
  const [contactDataLoading, setContactDataLoading] = useState(false);
  const scrollableContentRef = useRef(null);
  const [rolesProperties] = useRolesContext();
  const { blockNumber } = useNumbers(appProperties);
  const [blockNumberBtnLoading, setBlockNumberBtnLoading] = useState(false);
  const [blockNumberModalOpen, setBlockNumberModalOpen] = useState(false);
  const [integId, setIntegId] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(localStorage.getItem("currentActiveInboxTab") || "info");
  const { conversationContext } = useContext(ConvContext) || {};

  const hasPermisssionToBlockNumbers = useMemo(() => {
    return !rolesProperties?.currentRoleDetails || !checkUserHasModuleAccess(rolesProperties?.currentRoleDetails, "Blocked Numbers");
  }, [rolesProperties]);

  useEffect(() => {
    if (noteIdToBeFocused) {
      setActiveTab("notes");
      localStorage.setItem("currentActiveInboxTab", "notes");
    }
  }, [noteIdToBeFocused]);

  const fetchContactInfo = async () => {
    if (!toNumber || (!contactId && !sourceModule && !sourceId) || !leftServiceId) {
      setError("SELECT_CONTACT");
      return;
    }

    setContactDataLoading(true);
    setError(null);

    try {
      var targetUrl = `omessage/contact/${contactId}?leftServiceId=${leftServiceId}`;
      if ((!contactId || contactId === "new_contact") && sourceModule && sourceId) {
        targetUrl = `omessage/${leftServiceId}/contactInfo?sourceModule=${sourceModule}&sourceId=${sourceId}`;
      }
      const response = await fetchData(targetUrl, "GET", null, appProperties);

      // The useHttp hook returns full Axios response object
      // The actual API response is in response.data
      if (response) {
        const apiResponse = JSON.parse(response)?.data || {};
        if (apiResponse.success) {
          setContactData(apiResponse.data);
          // Scroll to top when new contact data is loaded
          setTimeout(() => {
            if (scrollableContentRef.current) {
              scrollableContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
            }
          }, 100);
        } else if (apiResponse.data === "Contact not found") {
          var phone = conversationContext?.[contactId]?.orgCustomer;
          var ownerDetails = appProperties?.allLicensedUserDetails?.find(
            (item) => item?.osyncUserId === conversationContext?.[contactId]?.contactOwnerEntityId
          );
          var ownerName = ownerDetails?.fullName || ownerDetails?.lastName || ownerDetails?.firstName || ownerDetails?.email;
          var firstName = conversationContext?.[contactId]?.firstName;
          var lastName = conversationContext?.[contactId]?.lastName;
          setContactData({
            phone: phone,
            ownerName: ownerName,
            firstName: firstName,
            lastName: lastName,
          });
        } else {
          setError("FETCH_FAILED");
        }
      }
    } catch (err) {
      console.error("Error fetching contact info:", err);
      setError("FETCH_FAILED");
    } finally {
      setContactDataLoading(false);
    }
  };

  useEffect(() => {
    if (fromNumber) {
      setIntegId(appProperties?.savedNumbers?.find((item) => item?.phoneNumber === fromNumber)?.integId);
    }
  }, [appProperties?.savedNumbers, fromNumber]);

  useEffect(() => {
    if (showContactInfo) {
      fetchContactInfo();
    }
    // Clear data when contact changes
    if (contactId && contactData && contactData.contactId !== contactId) {
      setContactData(null);
    }
  }, [contactId, toNumber, leftServiceId, showContactInfo]);

  if (!showContactInfo) return null;

  const handleBlockNumber = async (number) => {
    try {
      if (!checkUserHasModuleAccess(rolesProperties?.currentRoleDetails, "Blocked Numbers")) {
        HNotification.notify({ type: "error", message: "Error", description: "You don't have permission to block numbers" });
        return;
      }
      setBlockNumberBtnLoading(true);
      const response = await blockNumber(number);
      if (response) {
        successNotification("Number blocked successfully");
        handleBlockNumberSuccess();
      } else {
        HNotification.notify({ type: "error", message: "Error", description: "Failed to block number" });
      }
    } catch (e) {
      HNotification.notify({ type: "error", message: "Error", description: e });
    } finally {
      setBlockNumberBtnLoading(false);
      setBlockNumberModalOpen(false);
    }
  };
  return (
    <div className="contact-info-panel">
      <Card
        bordered={false}
        className="h-100 contact-info-card-body"
        title={
          error !== "SELECT_CONTACT" && (
            <div className="hs-w-95 mx-auto">
              <Row wrap={false} gutter={16}>
                <Col flex={"60px"}>{getContactAvatar(contactData, getDisplayNameOfCustomer(contactData), appProperties, true)}</Col>
                <Col flex={"auto"}>
                  <Row wrap={false}>
                    <Col flex={"auto"}>
                      {(() => {
                        const title = contactDataLoading
                          ? "Loading..."
                          : [contactData?.firstName, contactData?.lastName].filter(Boolean).join(" ") || `Unknown Contact`;
                        return (
                          <Tooltip title={title}>
                            <Text className="hs-fs-16 hs-fw-600" ellipsis={true}>
                              {title}
                            </Text>
                          </Tooltip>
                        );
                      })()}
                    </Col>
                    <Col flex={"60px"}>
                      <Text ellipsis className="hs-fs-12 hs-fw-400">
                        {contactData?.sourceModule && `- ${contactData?.sourceModule}`}
                      </Text>
                    </Col>
                  </Row>

                  {contactData?.recordLink && contactData?.recordLink !== "null" && (
                    <div>
                      <Link
                        baseUrl={contactData?.recordLink}
                        target="_blank"
                        externalLink
                        iconAtEnd={<div className="staticIconsSprite external-link-icon" />}
                      >
                        View in {contactData?.displayRecordLinkName || appProperties?.leftServiceDisplayName}
                      </Link>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          )
        }
      >
        {error !== "SELECT_CONTACT" ? (
          <Tabs
            destroyInactiveTabPane={true}
            className="select-contact-custom-tabs h-100"
            tabBarGutter={0}
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              localStorage.setItem("currentActiveInboxTab", key);
            }}
            items={[
              {
                key: "info",
                label: "Info",
                className: "select-contact-tab-content",
                children: (
                  <ContactDetails
                    contactId={contactId}
                    contactData={contactData}
                    contactDataLoading={contactDataLoading}
                    error={error}
                    fetchContactInfo={fetchContactInfo}
                    handleBlockNumber={handleBlockNumber}
                    showBlockNumberBtn={showBlockNumberBtn && hasPermisssionToBlockNumbers}
                    blockNumberModalOpen={blockNumberModalOpen}
                    setBlockNumberModalOpen={setBlockNumberModalOpen}
                    blockNumberBtnLoading={blockNumberBtnLoading}
                    rolesProperties={rolesProperties}
                    appProperties={appProperties}
                    scrollableContentRef={scrollableContentRef}
                    showSwitch={showSwitch}
                    showChatView={showChatView}
                    setShowChatView={setShowChatView}
                  />
                ),
              },
              contactId && contactId !== "new_contact"
                ? {
                    key: "notes",
                    label: "Notes",
                    className: "select-contact-tab-content",
                    children: (
                      <div className="h-100">
                        <Notes
                          appProperties={appProperties}
                          entityId={contactId}
                          integId={integId}
                          onNoteAdded={onNoteAdded}
                          onNoteUpdated={onNoteUpdated}
                          onNoteDeleted={onNoteDeleted}
                          activeTab={activeTab}
                          noteIdToBeFocused={noteIdToBeFocused}
                          setNoteIdToBeFocused={setNoteIdToBeFocused}
                        />
                      </div>
                    ),
                  }
                : null,
            ]}
          />
        ) : (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Text type="secondary">Select a contact to view</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ContactInfoPanel;
